import { filterListItems, type IActivityFilters } from '../activitiesFilter.js';
import type { IListItem } from './listItem.js';
import type { IWindowBounds } from './planWindow.js';
import { inWindow, itemStatusKey } from './promise.js';

/** The page's filters as a view reads them: the shared set, plus the Status
 *  checkboxes as the statuses hidden (absent: none). */
export interface IViewFilters extends IActivityFilters {
  hiddenStatuses?: readonly string[];
}

export interface IInViewContext {
  /** The SITE's today — the date filters and the stretched spans key off it. */
  today: string;
  /** The site's rest days, for the date preset's working week. */
  restDays?: Set<number>;
  /** The page's date window; null or absent: every date. */
  window?: IWindowBounds | null;
  /** The Status checkbox an item answers to in this view's vocabulary —
   *  by default an activity's rung and an issue's keyed status. */
  statusKey?: (item: IListItem) => string;
}

/**
 * The items one view of the Activities page holds — the ONE selector every
 * view runs, so the Daily List and the Progress Tracker can never disagree
 * about which activities are in view: the shared filters (`filterListItems`),
 * then the Status checkboxes in the view's own vocabulary, then the page's
 * date window, where a late promise runs on to today (`openSpan`). A view
 * narrows further only by what it is (the tracker: activities with areas),
 * never by a filter of its own reading.
 */
export function itemsInView(
  items: readonly IListItem[],
  filters: IViewFilters,
  { today, restDays, window = null, statusKey = itemStatusKey }: IInViewContext,
): IListItem[] {
  const hidden = filters.hiddenStatuses ?? [];
  return filterListItems(items, filters, today, restDays).filter(
    (item) => !hidden.includes(statusKey(item)) && (window === null || inWindow(item, today, window)),
  );
}
